import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_EnglishImageTranslator = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/english-image-translator',
			name: 'English Image Translator — Translate Photo to Any Language | Lift',
			description:
				'Translate English photos into Hindi, Malayalam, Urdu, Malay, and 100+ languages with AI. No watermark. Upload a picture, pick a language, and get a translated image in seconds. Try now!',
			url: 'https://liftapp.ai/english-image-translator',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/english-image-translator#product' },
				{ '@id': 'https://liftapp.ai/english-image-translator#howto' },
				{ '@id': 'https://liftapp.ai/english-image-translator#benefits' },
				{ '@id': 'https://liftapp.ai/english-image-translator#faq' },
				{ '@id': 'https://liftapp.ai/english-image-translator#why-lift' },
				{ '@id': 'https://liftapp.ai/english-image-translator#explore-tools' },
				{ '@id': 'https://liftapp.ai/english-image-translator#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/english-image-translator#product',
			name: 'English Image Translator Online',
			description:
				'Translate English photos into any language with AI. Upload an image, choose a target language, and get clean, layout-preserving translations in seconds — no watermark.',
			url: 'https://liftapp.ai/english-image-translator',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/english-image-translator#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your image',
					text: "Choose a photo from your device or take one using Lift's app",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Select your target language',
					text: 'Pick from popular languages like Hindi, Chinese, Urdu, French, or browse the full list of 100+ supported languages',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Translate your image',
					text: 'Tap"Translate Image" and let AI detect all text in your photo and render it in the chosen language while preserving layout and design',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download your result',
					text: 'Save your translated photo in high resolution, regenerate it, or start over with a new image and language',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/english-image-translator#benefits',
			name: 'Who can benefit from English Image Translator?',
			description:
				"Lift's AI image translator helps travelers, students, sellers, creators, and businesses translate text in photos while keeping the original design and context.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Travelers can instantly translate English signs, menus, tickets, and instruction labels into local languages while keeping full visual context',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Students can translate English textbook images, scanned slides, or research charts without manual transcription',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'E-commerce sellers can localize English product labels, packaging images, and promotional graphics for international buyers',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Content creators can translate English infographics, memes, and visual posts while keeping the original design intact',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Businesses can localize English banners, ads, and marketing visuals for international markets with one click',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'Benefits from AI Image Translate',
				description:
					'Translate text in images with Lift AI Image Translator while keeping the original layout and design.',
			},
		},
		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/english-image-translator#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What languages does the AI image translator support?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'The tool supports 100+ languages for translation, including Hindi, Malayalam, Urdu, Malay, Myanmar, Somali, Kannada, and many more.',
					},
				},
				{
					'@type': 'Question',
					name: 'Does the translator handle images with detailed or layered layouts?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. The AI is built to handle complex image layouts, including multi-column designs, overlapping elements, and stylized fonts. It detects and replaces text while keeping structure, spacing, and visual style intact.',
					},
				},
				{
					'@type': 'Question',
					name: 'Does translating text inside an image reduce its quality?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "No. Lift's AI processes and exports your translated photo at its original resolution. The image quality remains unchanged — only the text content is updated to your chosen language.",
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},
		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/english-image-translator#why-lift',
			name: "Why Lift's AI Image Translator stands out",
			description:
				"Lift's AI image translator delivers high-quality translations, intelligent rendering, cross-platform access, and an affordable workflow for fast localization.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Lift AI text from image translator',
				description:
					'Lift AI Image Translator benefits — high quality, intelligent rendering, cross-platform access, and affordability.',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/english-image-translator#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},
		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/english-image-translator#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},
		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'English Image Translator',
					item: 'https://liftapp.ai/english-image-translator',
				},
			],
		},
	],
};
